import { computed, inject, provide } from 'vue'
import { useRoute } from 'vue-router'

import useConfirmModal from '@/composables/common/modal/useConfirmModal'
import useAxiosResponse from '@/composables/common/useAxiosResponse'
import useToast from '@/composables/feedback/useToast'
import bookingArmService from '@/services/api/booking'
import { dateTextFormat } from '@/utils/date'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useBaseBookingDetailsView = () => {
  const route = useRoute()
  const { addToast } = useToast()
  const cancelBookingConfirmModal = useConfirmModal<string>(Symbol('cancel-booking-confirm-modal'))

  const bookingQuery = useAxiosResponse(async () => {
    return await bookingArmService.fetchBooking(
      route.params.id as string,
      { requestErrorEnabled: true }
    )
  }, {
    loadingMode: 'normal'
  })

  const cancelBookingMutation = useAxiosResponse<unknown, string>(async (bookingStatusReason) => {
    return await bookingArmService.cancelBooking(
      route.params.id as string,
      { booking_status_reason: bookingStatusReason },
      { requestErrorEnabled: true }
    )
  }, {
    onSuccess () {
      addToast({
        type: 'common',
        message: `Booking ID ${bookingQuery.data.value?.booking_no} has been cancelled`,
        second: 5
      })
      bookingQuery.execute()
    },
    loadingMode: 'normal'
  })

  // #region Getters

  const patientFullName = computed((): string => {
    return `${bookingQuery.data.value?.patient_first_name} ${bookingQuery.data.value?.patient_last_name}`
  })

  const visitDate = computed((): string | undefined => {
    return bookingQuery.data.value && dateTextFormat(bookingQuery.data.value.visit_date)
  })

  // #endregion

  // #region Actions

  const handleShowCancelBookingConfirmModal = async (): Promise<void> => {
    const { isCanceled, data } = await cancelBookingConfirmModal.reveal()

    if (isCanceled) {
      return
    }

    cancelBookingMutation.execute(data)
  }

  // #endregion

  return {
    bookingQuery,
    cancelBookingMutation,
    cancelBookingConfirmModal,
    // Getters
    patientFullName,
    visitDate,
    // Actions
    handleShowCancelBookingConfirmModal
  }
}

export const defaultKey = Symbol('view:booking-details')

export default function useBookingDetailsView (): ReturnType<typeof useBaseBookingDetailsView> {
  const result = inject<ReturnType<typeof useBaseBookingDetailsView> | undefined>(defaultKey, undefined)

  if (result === undefined) {
    const newResult = useBaseBookingDetailsView()
    provide(defaultKey, newResult)
    return newResult
  }

  return result
}
